import {
  ComponentClass,
  CSSProperties,
  FC,
  MouseEventHandler,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { SortableElement, SortableElementProps, SortableHandle } from 'react-sortable-hoc'
import { t } from 'i18next'
import { FormContainer, FormTab } from 'src/types'
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material'
import { Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material'

import {
  useCreateContainerTabMutation,
  useUpdateContainerTabMutation,
} from '@redux/api/container.api'

import { getTabById } from '@helpers'

import { ElementContextMenu } from '../ActionMenu/ElementContextMenu'
import { TextPopover } from '../ActionMenu/TextPopover'
import { useActionMenu } from '../ActionMenu/useActionMenu'
import { FieldsConfigContext } from '../FieldsConfig'

type TabButtonProps = {
  tab: FormTab
  index: number
  isTabSingle: boolean
  containerId: number
  currentTabButton: number
  onDelete: (tabId: number) => void
  handleSetCurrentTabButton: (value: number) => void
}

export const TabButton: FC<TabButtonProps> = ({
  tab,
  index,
  isTabSingle,
  containerId,
  currentTabButton,
  onDelete,
  handleSetCurrentTabButton,
}) => {
  const { containers, setContainers, activeMenuElementId, setActiveMenuElementId } =
    useContext(FieldsConfigContext)

  const [createTab] = useCreateContainerTabMutation()
  const [updateTab] = useUpdateContainerTabMutation()
  const creationInProgress = tab.id === -1
  const [editingInProgress, setContainerEditing] = useState(false)
  const [newTabTitle, setNewTabTitle] = useState(tab.title)
  const [isTabHidden, setIsTabHidden] = useState(false)

  useEffect(() => {
    if (tab.titleHidden !== undefined) {
      setIsTabHidden(tab.titleHidden)
    }
  }, [])

  const handleDeleteButtonClick: MouseEventHandler<SVGSVGElement> = event => {
    event.stopPropagation()

    onDelete(tab.id)
  }

  const handleTabButtonClick = () => {
    handleSetCurrentTabButton(index)
  }

  const handleCancelTabCreationOrEdit = () => {
    if (creationInProgress) {
      const newContainers = structuredClone(containers) as FormContainer[]
      const { container: newContainer } = getTabById(newContainers, containerId, tab.id)

      if (!newContainer) {
        return
      }

      newContainer.tabs.pop()

      setContainers(newContainers)
    }

    setContainerEditing(false)
  }

  const handleSetNewTabTitle = (value: string) => setNewTabTitle(value)

  const handleSetTextElementEditing = (value: boolean) => setContainerEditing(value)

  const {
    popoverAnchorEl,
    menuPosition,
    isPopoverOpen,
    handleOpenPopover,
    handlePopoverClose,
    handleCancelPoppedEdit,
    handleElementContextClick,
    handleCloseMenu,
  } = useActionMenu({ handleSetEditing: handleSetTextElementEditing })

  const handleTabClick = e => {
    handleElementContextClick(e)
    setActiveMenuElementId(tab.id)
  }

  const handleTabMenuClose = () => {
    handleCloseMenu()
    setActiveMenuElementId(null)
  }

  const handleSaveButtonClick = () => {
    if (!newTabTitle) {
      return
    }

    if (editingInProgress) {
      handlePopoverClose()
      handleTabMenuClose()
    }

    const newContainers = structuredClone(containers) as FormContainer[]
    const { tab: newTab, container: newContainer } = getTabById(newContainers, containerId, tab.id)

    if (!newTab || !newContainer) {
      return
    }

    const { id, lines, ...body } = { ...newTab, title: newTabTitle }

    newContainer.tabs[index] = { ...newTab, title: newTabTitle }
    setContainers(newContainers)

    if (creationInProgress) {
      createTab({ ...body, containerId })
    }

    if (editingInProgress) {
      updateTab({ ...body, id, containerId })
    }
  }

  const handleUpdateTabData = (titleHidden: boolean) => {
    if (!tab.id) {
      return
    }

    const { id, lines, ...body } = {
      ...tab,
      titleHidden,
    }

    setIsTabHidden(titleHidden)
    updateTab({ ...body, id, containerId })
    handleCloseMenu()
  }

  const CreationInput = () => (
    <>
      <TextField
        autoFocus
        placeholder={t('fieldsConfig.placeholder.title')}
        size={'small'}
        sx={{ ml: 0.5, width: 100 }}
        value={newTabTitle}
        variant={'standard'}
        onChange={event => handleSetNewTabTitle(event.target.value)}
      />
      <IconButton onClick={handleSaveButtonClick}>
        <CheckIcon />
      </IconButton>
      <IconButton onClick={handleCancelTabCreationOrEdit}>
        <ClearIcon />
      </IconButton>
    </>
  )

  const DragHandle = SortableHandle(({ style }: { style: CSSProperties }) => (
    <Box display={'flex'}>
      <DragIndicatorIcon sx={{ ...style, fontSize: '1.25rem', cursor: 'grab' }} />
    </Box>
  ))

  const TabButtonElement: ComponentClass<SortableElementProps & { children: ReactNode }> =
    SortableElement(({ children }: { children: ReactNode }) => (
      <Grid item p={0.5} xs={3}>
        {children}
      </Grid>
    ))

  return (
    <>
      <TextPopover
        handleCancelPoppedEdit={handleCancelPoppedEdit}
        handlePopoverClose={handlePopoverClose}
        handleSaveText={handleSaveButtonClick}
        handleSetText={handleSetNewTabTitle}
        isPopoverOpen={isPopoverOpen}
        popoverAnchorEl={popoverAnchorEl}
        staticTextTitle={tab.title}
        textTitle={newTabTitle}
      />
      {activeMenuElementId === tab.id && (
        <ElementContextMenu
          displayDeleteOption={false}
          displayExpandableOption={false}
          displayLabelAlignmentOption={false}
          displayLabelParamsOption={isTabSingle}
          elementTitleHidden={isTabHidden}
          handleFieldMenuClose={handleTabMenuClose}
          handleOpenEdit={handleOpenPopover}
          handleSetElementTitleHidden={handleUpdateTabData}
          menuPosition={menuPosition}
        />
      )}
      {creationInProgress ? (
        <CreationInput />
      ) : (
        <TabButtonElement index={index}>
          <Button
            fullWidth
            component={'div'}
            endIcon={<ClearIcon onClick={handleDeleteButtonClick} />}
            startIcon={<DragHandle />}
            variant={currentTabButton === index ? 'contained' : 'outlined'}
            sx={{
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              justifyContent: 'space-between',
            }}
            onClick={handleTabButtonClick}
            onContextMenu={handleTabClick}
          >
            <Typography noWrap sx={{ textTransform: 'none' }} title={tab.title}>
              {tab.title}
            </Typography>
          </Button>
        </TabButtonElement>
      )}
    </>
  )
}
